import * as z from "zod";
import { StorefrontOfferSchema } from "../offers";
import { AiDiscoveryFunnelContextSchema } from "../primitives/ai-discovery-config";
import { BooleanSchema } from "../primitives/boolean";
import { BrandTokensSchema } from "../primitives/brand";
import { HubspotRegionSchema } from "../primitives/hubspot";
import { SectionThemeSchema, StorefrontThemeSchema } from "../primitives/theme";
import { CustomCardsSchema, CustomCardsStyleSchema } from "../primitives/custom-cards";
import { BackgroundPropValueSchema, ProductArrayItemSchema } from "../primitives/editor";
import { ActiveEntitlementsSchema } from "../primitives/entitlement";
import {
  FaqItemTitleStyleSchema,
  ProductFaqItemSchema,
  StorefrontFaqSectionDataSchema,
  StorefrontPageAnnouncementSectionDataSchema,
  StorefrontPageCustomerReviewsSectionDataSchema,
  StorefrontPageSectionUnionSchema,
  StorefrontTextVariantSchema,
  StorefrontTypographyStyleSchema,
} from "../primitives/legacy-sections";
import {
  BundleSchema,
  CustomizeOptionSchema,
  CustomVariantsMediaOptionsSchema,
  CustomVariantsMediaSchema,
  MediaLabelsSchema,
  ProductKeyBenefitsSchema,
  ProductReviewsSummarySchema,
  type CustomVariantMediaItem,
  type CustomVariantsMedia,
} from "../primitives/product-data";
import { ShopifyProxyTokenSchema } from "./shopify-proxy";
import { OnlineStoreHostnameSchema } from "./shopify-shop";
import {
  BuyBoxConfigSchema,
  CookieConsentProviderSchema,
  DiscountOffTextTransformSchema,
  ShopConfigSchema,
  ShopifyPagesConfigSchema,
} from "../primitives/shop";
import { CartAttributesSchema } from "../primitives/shopify";
import { SourceEntitySchema } from "../primitives/source-entity";
import { CustomFontSchema } from "../primitives/typography";
import { CartSellingPlanUpgradeRuleSchema, CartSettingsSchema } from "./cart-settings";
import { CompiledJsxSectionSchema } from "./compiled-sections";
import { IconPayloadSchema } from "./icons";
import { OffersConfigSchema } from "./offers-settings";
import { StickyCTAActionConfigSchema } from "./sticky-cta";
import { StorefrontClientScriptSchema } from "./storefront-client-script";

export * from "./sticky-cta";

export const StorefrontMediaTypeSchema = z.enum(["AVATAR", "STORY", "BACKGROUND", "PHOTO"]);
export type StorefrontMediaType = z.infer<typeof StorefrontMediaTypeSchema>;

/** The legacy editor's single-section edit modes. Remove with editor-legacy. */
export const SingleEditMode = z.enum(["globalSection", "stickyCTA"]);
export type SingleEditMode = z.infer<typeof SingleEditMode>;

// Re-export types for backward compatibility
export type { CustomVariantMediaItem, CustomVariantsMedia };

export const StorefrontPageTreeSchema = z.object({
  id: z.number(),
  sections: StorefrontPageSectionUnionSchema.array(),
  sectionRefs: z.record(z.number(), StorefrontPageSectionUnionSchema).optional(),
  /** Own themes of the referenced global sections and buy boxes that have one. */
  sectionRefThemes: z.record(z.number(), SectionThemeSchema).optional(),
  hasStaticIds: z.boolean().optional(),
});
export type StorefrontPageTree = z.infer<typeof StorefrontPageTreeSchema>;

export const StorefrontTypographySchema = z.partialRecord(StorefrontTextVariantSchema, StorefrontTypographyStyleSchema);
export type StorefrontTypography = z.infer<typeof StorefrontTypographySchema>;

export const StorefrontThemeConfigSchema = z.object({
  theme: StorefrontThemeSchema.default({}),
});
export type StorefrontThemeConfig = z.infer<typeof StorefrontThemeConfigSchema>;

export const PageType = {
  HOME: "HOME",
  PDP: "PDP",
  CART: "CART",
} as const;
export const StorefrontPageTypeSchema = z.enum(PageType);
export type StorefrontPageType = z.infer<typeof StorefrontPageTypeSchema>;
export const isStorefrontPageType = (pageType: string): pageType is StorefrontPageType => {
  return Object.values(PageType).includes(pageType as StorefrontPageType);
};

export const ViewportMode = z.enum(["mobile", "desktop"]);
export type ViewportMode = z.infer<typeof ViewportMode>;

export const StorefrontMediaSchema = z.object({
  id: z.number(),
  url: z.string(),
  fileType: z.string(),
  shopId: z.number().optional(),
  key: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
});
export type StorefrontMedia = z.infer<typeof StorefrontMediaSchema>;

export const StorefrontMediaFileSchema = StorefrontMediaSchema.extend({
  mediaType: StorefrontMediaTypeSchema.nullish(),
});
export type StorefrontMediaFile = z.infer<typeof StorefrontMediaFileSchema>;

export const StorefrontPageExperimentSchema = z.object({
  id: z.number(),
  url: z.string(),
  percentage: z.number(),
});
export type StorefrontPageExperiment = z.infer<typeof StorefrontPageExperimentSchema>;

export const StorefrontPageDialogSectionDataSchema = z.object({
  buttonText: z.string().optional(),
  imageUrl: z
    .union([
      z.url(),
      z.url().array(),
      z
        .object({
          label: z.string(),
          imageUrl: z.url(),
          device: z.enum(["desktop", "mobile"]).optional(),
        })
        .array(),
    ])
    .nullish(),
  title: z.string().optional(),
  subTitle: z.string().optional(),
  content: z.string().optional(), // HTML string
  style: z.any().optional(),
  textAlign: z.enum(["left", "center", "right"]).optional(),
});
export type StorefrontPageDialogSectionData = z.infer<typeof StorefrontPageDialogSectionDataSchema>;

export const StorefrontPageSizeGuideSectionDataSchema = z.object({
  buttonText: z.string().optional(),
  imageUrl: z.url().nullish().or(z.literal("")),
  title: z.string().optional(),
  subTitle: z.string().optional(),
  content: z.string().optional(), // HTML string
  style: z.any().optional(),
});
export type StorefrontPageSizeGuideSectionData = z.infer<typeof StorefrontPageSizeGuideSectionDataSchema>;

export const FindMySizeSelectorSchema = z.object({
  id: z.string(),
  label: z.string(),
  options: z.array(
    z.object({
      value: z.string(),
      label: z.string().optional(),
    }),
  ),
});
export type FindMySizeSelector = z.infer<typeof FindMySizeSelectorSchema>;

export const FindMySizeConfigSchema = z.object({
  title: z.string().optional(),
  defaultExpanded: z.boolean().optional(),
  selectors: z.array(FindMySizeSelectorSchema),
  lookupTable: z.record(z.string(), z.string()),
  recommendationText: z.string().optional(),
  targetOptionName: z.string().optional(),
});
export type FindMySizeConfig = z.infer<typeof FindMySizeConfigSchema>;

export const StorefrontPageSiblingOptionSchema = z.object({
  optionName: z.string(),
  optionLabel: z.string().optional(),
  modalStepLabel: z.string().optional(),
  values: z
    .object({
      label: z.string(),
      productId: z.string(),
      colorHex: z.union([z.string(), BackgroundPropValueSchema]).optional(),
      customCard: z
        .object({
          default: z.string(),
          selected: z.string().optional(),
        })
        .optional(),
    })
    .array(),
  withImage: z.boolean().optional(),
});
export type StorefrontPageSiblingOption = z.infer<typeof StorefrontPageSiblingOptionSchema>;

export const StorefrontPageProductOfferSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  buttonText: z.string(),
  badge: z.string().optional(),
  link: z.string(),
});
export type StorefrontPageProductOffer = z.infer<typeof StorefrontPageProductOfferSchema>;

export const StorefrontPageProductKeyBenefitsDataSchema = ProductKeyBenefitsSchema;
export type StorefrontPageProductKeyBenefitsData = z.infer<typeof StorefrontPageProductKeyBenefitsDataSchema>;

export const StorefrontPageProductCustomizeOptionSchema = CustomizeOptionSchema;
export type StorefrontPageProductCustomizeOption = z.infer<typeof StorefrontPageProductCustomizeOptionSchema>;

export const StorefrontPageSellingPlanBenefitSchema = z.object({
  text: z.string(),
  iconImageUrl: z.string().optional(),
  withIcon: z.boolean().optional(),
});
export type StorefrontPageSellingPlanBenefit = z.infer<typeof StorefrontPageSellingPlanBenefitSchema>;

const PreselectedCardStyleSchema = CustomCardsStyleSchema;

const QuantitySelectorConfigSchema = z.object({
  enabled: z.boolean().optional().default(false),
  // Predefined quantity options, e.g. [1, 2, 3, 4, 5]. Required if range not set.
  options: z.number().array().min(1).optional(),
  // Shorthand: { start: 1, end: 5 } generates [1,2,3,4,5]. Used when options not set.
  range: z.object({ start: z.number(), end: z.number() }).optional(),
  style: z
    .object({
      borderColor: z.string().optional(),
      backgroundColor: z.string().optional(),
      badge: z
        .object({
          color: z.string().optional(),
          backgroundColor: z.string().optional(),
          selectedColor: z.string().optional(),
          selectedBackgroundColor: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});
export type QuantitySelectorConfig = z.infer<typeof QuantitySelectorConfigSchema>;

export const PreselectedCardSchema = z
  .object({
    // Explicit card identifier for content mapping
    id: z.string().optional(),
    /** When set, resolve variant/optionValues against this product instead of the PDP. With optionsToShowAbove, option pickers stay on the PDP while the selected card can switch which product’s variant is used; only options listed in optionsToShowAbove are applied when resolving the external product variant. The external product does not need to be in the funnel — it is fetched client-side from the storefront API. */
    externalProductId: z.string().optional(),
    // variantId is optional - can use either variantId OR optionValues
    variantId: z.string().optional(),
    // optionValues: map of option name to value (e.g., { "Size": "S", "Sleeve": "Long sleeves" })
    optionValues: z.record(z.string(), z.string()).optional(),
    quantity: z.number().optional(),
    extraDiscount: z.number().optional(),
    sellingPlanId: z.string().optional(),
    title: z.string(),
    badge: z.string().optional(),
    content: z.string().optional(),
    sellingPlanMonths: z.number().optional(),
    // HTML + Handlebars template string for fully customizable card content
    // When present, replaces default inner content UI
    customInnerCard: z.string().optional(),
    style: PreselectedCardStyleSchema.optional(),
    lineItemProperties: z
      .array(
        z.object({
          key: z.string(),
          value: z.string(),
        }),
      )
      .optional(),
  })
  .refine(
    (data) =>
      !!data.externalProductId ||
      !!data.variantId ||
      data.quantity != null ||
      (data.optionValues != null && Object.keys(data.optionValues).length > 0),
    {
      message: "Either variantId, quantity, optionValues, or externalProductId must be provided",
    },
  );
export type PreselectedCard = z.infer<typeof PreselectedCardSchema>;

export const PreselectedCardsSchema = z.object({
  modalStepLabel: z.string().optional(),
  optionsToShowAbove: z.string().array().optional(),
  label: z.string().optional(),
  defaultSelectedCardId: z.string().optional(),
  cards: PreselectedCardSchema.array().optional(),
  cardsLayout: z.enum(["horizontal", "vertical"]).optional(),
  withRadioButtons: z.boolean().optional().default(true),
  containers: z
    .object({
      quantitySelector: QuantitySelectorConfigSchema.optional(),
      title: z.string().optional(),
      isCollapsible: z.boolean().optional(),
      content: z.string().optional(),
      // Maps card ID -> content HTML for per-card container content
      contentMap: z.record(z.string(), z.string()).optional(),
      submitButtonText: z.string().optional(),
      footer: z.string().optional(),
      submitButtonStyle: z
        .object({
          backgroundColor: z.string().optional(),
          color: z.string().optional(),
          borderColor: z.string().optional(),
          boxShadow: z.string().optional(),
          disabled: z
            .object({
              backgroundColor: z.string().optional(),
              color: z.string().optional(),
              borderColor: z.string().optional(),
              boxShadow: z.string().optional(),
            })
            .optional(),
        })
        .optional(),
      style: z
        .object({
          backgroundColor: z.string().optional(),
          border: z.string().optional(),
          boxShadow: z.string().optional(),
          padding: z.string().optional(),
          title: z
            .object({
              fontSize: z.enum(["small", "base", "large"]).optional(),
            })
            .optional(),
        })
        .optional(),
      innerCards: PreselectedCardSchema.array(),
      defaultVariantId: z.string().optional(),
      defaultSellingPlanId: z.string().optional(),
      // Option values for resolving variant when no inner cards and no defaultVariantId (e.g. { "Size": "28 Packs" })
      defaultOptionValues: z.record(z.string(), z.string()).optional(),
    })
    .array()
    .optional(),
  cardStyle: PreselectedCardStyleSchema.optional(),
});
export type PreselectedCards = z.infer<typeof PreselectedCardsSchema>;

export const StorefrontPageProductSubscriptionCustomizationSchema = z.object({
  // Main subscription group title (e.g., "Subscribe & Save")
  title: z.string().optional(),
  // Label for the plans selector (e.g., "Select delivery frequency")
  plansLabel: z.string().optional(),
  // Default selling plan ID
  defaultPlanId: z.string().optional(),
  // Custom names for individual selling plans, keyed by selling plan ID
  planNames: z.record(z.string(), z.string()).optional(),
  // Custom order for selling plans (array of selling plan IDs)
  planOrder: z.string().array().optional(),
  // Custom quantities for selling plans, keyed by selling plan ID
  planQuantities: z.record(z.string(), z.number()).optional(),
  // Custom compare at prices for selling plans, keyed by selling plan ID
  planCompareAtPrices: z.record(z.string(), z.number()).optional(),
  priceFormatting: z.string().optional(),
  // Custom key benefits for selling plans, keyed by selling plan ID
  planBenefits: z.record(z.string(), StorefrontPageSellingPlanBenefitSchema.array()).optional(),
  // Custom html footers for selling plans, keyed by selling plan ID
  planFooters: z.record(z.string(), z.string()).optional(),
  // Display variant for selling plans ("default" = tabs/select, "cards" = vertically stacked cards)
  planVariant: z.enum(["default", "cards", "stacked"]).optional(),
  // Badge display preference: "percentage" to show percentage discount, "fixed" to show fixed amount (if available)
  badgeDisplayType: z.enum(["percentage", "fixed"]).optional(),
  withBadge: z.boolean().optional(),
  // One-time purchase customization
  oneTimePurchase: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
      compareAtPrice: z.number().optional(),
    })
    .optional(),
  // Aggregate multiple selling plan groups into a single subscription box
  aggregateSellingPlanGroups: z.boolean().optional(),
  // Select a specific selling plan group by name (takes precedence over default last group)
  selectedSellingPlanGroupName: z.string().optional(),
  /** Handlebars template for cart line subscription upgrade button. Variables: sellingPlanDiscount, funnelId, variantId */
  cartLineItemSubscriptionUpgradeButtonText: z.string().optional(),
  // Per-variant override: variant external ID -> array of selling plan IDs + "one-time" to show.
  // When present for selected variant, replaces allocation-based filtering.
  visibleSubscriptionOptionsByVariant: z.record(z.string(), z.string().array()).optional(),
  // completely customizable cards for subscription flow
  customCards: CustomCardsSchema.optional(),
});
export type StorefrontPageProductSubscriptionCustomization = z.infer<
  typeof StorefrontPageProductSubscriptionCustomizationSchema
>;

const CurrencyPriceSchema = z.object({
  currency: z.string(),
  price: z.number(),
  compareAtPrice: z.number(),
});

export const StorefrontPageProductDataSchema = z.object({
  productId: z.number().optional(),
  externalId: z.string(),
  campaignId: z.number(),
  createdAt: z.string().optional(),
  media: StorefrontMediaSchema.array(),
  data: z
    .object({
      icons: IconPayloadSchema.optional(),
      customUrl: z.string().optional(),
      title: z.string().optional(),
      priceFormatting: z.string().optional(),
      customCompareAtPrice: z.number().optional(),
      hideBuyBoxProductHeader: z.boolean().optional(),
      mainMedia: z.number().optional(),
      productReview: z.string().optional(),
      reviewsSummary: ProductReviewsSummarySchema.optional(),
      productStories: z.number().array().optional(),
      discount: z.number().optional(),
      ctaBottomContent: z.string().optional(),
      bottomContent: z.string().optional(),
      genericContentBelowCTA: z.string().optional(),
      compiledGenericContentBelowCTA: CompiledJsxSectionSchema.nullish(),
      desktopGenericContentBelowCTA: z.string().nullish(),
      compiledDesktopGenericContentBelowCTA: CompiledJsxSectionSchema.nullish(),
      genericContentBelowProductImage: z
        .object({
          default: z.string().nullish(),
          compiledDefault: CompiledJsxSectionSchema.nullish(),
          desktop: z.string().nullish(),
          compiledDesktop: CompiledJsxSectionSchema.nullish(),
        })
        .optional(),
      genericContentBelowProductHeader: z.string().optional(),
      compiledGenericContentBelowProductHeader: CompiledJsxSectionSchema.nullish(),
      secondaryButton: z
        .object({
          text: z.string(),
          actionType: z.enum(["add_to_cart"]),
          variantId: z.string().optional(),
          sellingPlanId: z.string().optional(),
          quantity: z.number().optional(),
          style: z
            .object({
              variant: z.enum(["solid", "secondary", "link"]).optional(),
              color: z.string().optional(),
              backgroundColor: z.string().optional(),
              size: z.enum(["sm", "md", "lg"]).optional(),
            })
            .optional(),
        })
        .optional(),
      productCardInfo: z.string().optional(),
      hideProductFormButton: z.boolean().optional(),
      subTitle: z.string().optional(),
      faqItems: ProductFaqItemSchema.array().optional(),
      faqStyle: StorefrontFaqSectionDataSchema.shape.faqStyle.optional(),
      faqItemTitleStyle: FaqItemTitleStyleSchema.optional(),
      keyBenefits: StorefrontPageProductKeyBenefitsDataSchema.optional(),
      excludedImageIds: z.string().array().optional(),
      reviews: StorefrontPageCustomerReviewsSectionDataSchema.extend({
        isAboveProductTitle: z.boolean().optional(),
      }).optional(),
      sizeGuide: StorefrontPageSizeGuideSectionDataSchema.optional(),
      dialog: StorefrontPageDialogSectionDataSchema.optional(),
      findMySize: FindMySizeConfigSchema.optional(),
      siblingOptions: StorefrontPageSiblingOptionSchema.array().optional(),
      isSiblingOptionsRenderedBelowVariantSelectors: z.boolean().optional(),
      optionsToHide: z.string().array().optional(),
      variantsMedia: CustomVariantsMediaSchema,
      variantsMediaOptions: CustomVariantsMediaOptionsSchema.optional(),
      customizeOptions: z.record(z.string(), StorefrontPageProductCustomizeOptionSchema).array().optional(),
      offers: StorefrontPageProductOfferSchema.array().optional(),
      tags: z
        .object({
          title: z.string().optional(),
          icon: z.string().optional(),
        })
        .array()
        .optional(),
      productDescription: z.string().optional(),
      subscription: StorefrontPageProductSubscriptionCustomizationSchema.optional(),
      subscriptionBeforeVariantSelectors: z.boolean().optional(),
      currencyPrices: z.array(CurrencyPriceSchema).optional(),
      // Preselected cards - when configured, replaces variant selectors and subscription flow with card selection
      preselectedCards: PreselectedCardsSchema.optional(),
      bundle: BundleSchema.optional(),
      variantSelectModalSubtitle: z.string().optional(),
      /** Products to suggest in cart cross-sell banner when this product is in cart */
      crossSellProducts: z.array(ProductArrayItemSchema).optional(),
      mediaLabels: MediaLabelsSchema.optional(),
      frontrow: z
        .object({
          productId: z.number(),
          isEnabled: z.boolean(),
        })
        .optional(),
      desktopGenericGallery: z.string().optional(),
      compiledDesktopGenericGallery: CompiledJsxSectionSchema.nullish(),
    })
    .catchall(z.any()),
});
export type StorefrontPageProductData = z.infer<typeof StorefrontPageProductDataSchema>;

export const StorefrontFunnelFeaturedProductSchema = z.object({
  externalId: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
  price: z.coerce.number().optional(),
  currencyCode: z.string().optional(),
  compareAtPrice: z.coerce.number().optional(),
});
export type StorefrontFunnelFeaturedProduct = z.infer<typeof StorefrontFunnelFeaturedProductSchema>;

export const ProductOptionColorSwatchSelectorSchema = z.object({
  variant: z.enum(["default", "radio"]).optional(),
  borderWidth: z.number().optional(),
  borderColor: z.string().optional(),
});
export type ProductOptionColorSwatchSelector = z.infer<typeof ProductOptionColorSwatchSelectorSchema>;

export const StorefrontShopConfigSchema = z.object({
  isWhiteLabeled: ShopConfigSchema.shape.isWhiteLabeled,
  shopifyAnalyticsStorefrontAccessToken: z.string().optional(),
  shopifyAnalyticsMigrationEnabled: ShopConfigSchema.shape.shopifyAnalyticsMigrationEnabled,
  trackAddToCartOnDirectCheckout: ShopConfigSchema.shape.trackAddToCartOnDirectCheckout,
  storefrontAccessTokenOverride: ShopConfigSchema.shape.storefrontAccessTokenOverride,
  metaPixelId: z.string().optional(),
  metaPixelIds: z.string().array().optional(),
  gtmId: z.string().optional(),
  gtmIds: z.string().array().optional(),
  googleAnalyticsId: z.string().optional(),
  humanzPixelId: z.string().optional(),
  axonPixelId: z.string().optional(),
  northbeamPixelId: z.string().optional(),
  polarAnalyticsShopUrl: z.string().optional(),
  tiktokPixelId: z.string().optional(),
  elevarId: z.string().optional(),
  fontFamily: z.string().optional(),
  fontUrl: z.string().optional(),
  blotoutTagUrl: z.string().optional(),
  oneTrustDomainScriptId: ShopConfigSchema.shape.oneTrustDomainScriptId,
  pandectesRulesUrl: ShopConfigSchema.shape.pandectesRulesUrl,
  cookieConsentProvider: CookieConsentProviderSchema.optional(),
  isTripleWhaleEnabled: z.boolean().optional(),
  isConsentBannerEnabled: z.boolean().optional(),
  isExternalProductPage: z.boolean().optional(),
  isKlaviyoEnabled: z.boolean().optional(),
  klaviyoCompanyId: z.string().optional(),
  junip: z
    .object({
      storeKey: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  stamped: z
    .object({
      storeHash: z.string().optional(),
      apiPublicKey: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  trustpilot: z
    .object({
      businessUnitId: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  okendo: z
    .object({
      subscriberId: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  yotpo: z
    .object({
      appKey: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  judgeme: z
    .object({
      publicToken: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  bazaarvoice: z
    .object({
      clientName: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  reviewsio: z
    .object({
      storeId: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  postscript: z
    .object({
      shopId: z.string().optional(),
      isEnabled: z.boolean(),
      tripleWhaleFormSubmitEventName: z.string().optional(),
    })
    .optional(),
  alia: z
    .object({
      scriptSrc: z.string().optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  hubspot: z
    .object({
      portalId: z.string().optional(),
      region: HubspotRegionSchema.optional(),
      isEnabled: z.boolean(),
    })
    .optional(),
  globalCSS: z.string().optional(),
  defaultProductCtaText: z.string().optional(),
  shopifyPages: ShopifyPagesConfigSchema.optional(),
  buyBoxConfig: BuyBoxConfigSchema.optional(),
  hideOutOfStockOptions: z.boolean().optional(),
  // Subscription configuration (can be overridden at product level)
  subscription: StorefrontPageProductSubscriptionCustomizationSchema.optional(),
  intelligems: z
    .object({
      organizationId: z.string().optional(),
    })
    .optional(),
  cartAttributes: CartAttributesSchema.optional(),
  cartConfig: CartSettingsSchema.optional(),
  globalAnnouncementBar: StorefrontPageAnnouncementSectionDataSchema.extend({
    enabled: z.boolean(),
  }).optional(),
  customEvents: z
    .object({
      event_name: z.string(),
      event_data: z.any(),
    })
    .array()
    .optional(),
  thirdPartyCartApp: z
    .object({
      appName: z.string(),
    })
    .optional(),
  imageFit: z.enum(["cover", "contain"]).optional(),
  productOptionColorSwatchSelector: ProductOptionColorSwatchSelectorSchema.optional(),
  checkoutUrl: z.string().optional(),
  signCheckoutUrl: BooleanSchema.optional().describe("Set by the server when the shop has a checkout secret."),
  externalCart: z
    .object({
      provider: z.enum(["magento"]),
      endpoint: z.string(),
      checkoutUrl: z.string(),
      cookie: z.object({ name: z.string(), domain: z.string() }).optional(),
    })
    .optional(),
  microsoftClarityId: z.string().optional(),
  customSearchParamsExternalNavigation: z.record(z.string(), z.string()).optional(),
  priceFormatting: z
    .object({
      roundUp: z.boolean().optional(),
      toFixed: z.number().optional().describe("Number of decimal places to round to, default is 2"),
    })
    .optional(),
  discountOffTextTransform: DiscountOffTextTransformSchema.optional(),
  offersConfig: OffersConfigSchema.optional(),
});
export type StorefrontShopConfig = z.infer<typeof StorefrontShopConfigSchema>;

export const StorefrontShopTypeSchema = z.enum(["SHOPIFY", "MANUAL"]);
export type StorefrontShopType = z.infer<typeof StorefrontShopTypeSchema>;
export const StorefrontShopSchema = z.object({
  id: z.number(),
  shopifyId: z.string().optional(),
  logo: z.string().nullish(),
  name: z.string(),
  domain: z.string(),
  onlineStoreDomain: OnlineStoreHostnameSchema.optional(),
  onlineStoreDomainFallback: z.boolean().optional(),
  url: z.string(),
  config: StorefrontShopConfigSchema.optional(),
  customFonts: CustomFontSchema.array().optional(),
  scripts: StorefrontClientScriptSchema.array().optional(),
  currency: z.string(),
  language: z.string(),
  subdomain: z.string().optional(),
  favicon: z.string().nullish(),
  provider: StorefrontShopTypeSchema,
});
export type StorefrontShop = z.infer<typeof StorefrontShopSchema>;

export const StorefrontPageProgramSchema = z.object({
  id: z.number(),
  name: z.string().nullish(),
  discount: z.number(),
  products: z
    .object({
      externalId: z.string(),
    })
    .array(),
  mainProductId: z.number().nullish(),
  mainVideoId: z.number().nullish(),
});
export type StorefrontPageProgram = z.infer<typeof StorefrontPageProgramSchema>;

export const StorefrontPageVirtualProductSchema = z.object({
  id: z.number(),
  data: StorefrontPageProductDataSchema.shape.data.nullish(),
  products: z
    .object({
      id: z.number(),
      externalId: z.string(),
    })
    .array(),
});
export type StorefrontPageVirtualProduct = z.infer<typeof StorefrontPageVirtualProductSchema>;

export const StorefrontPageSectionsSchema = StorefrontPageSectionUnionSchema.array();
export type StorefrontPageSections = z.infer<typeof StorefrontPageSectionsSchema>;

/**
 * Robots directive configuration for SEO
 */
export const StorefrontSeoRobotsSchema = z.object({
  index: z.boolean().optional().default(true),
  follow: z.boolean().optional().default(true),
  maxSnippet: z.number().optional(),
  maxImagePreview: z.enum(["none", "standard", "large"]).optional(),
  maxVideoPreview: z.number().optional(),
});
export type StorefrontSeoRobots = z.infer<typeof StorefrontSeoRobotsSchema>;

/**
 * Enhanced SEO configuration for AEO optimization
 */
export const StorefrontFunnelSeoSchema = z.object({
  // Core SEO fields
  enabled: z.boolean().default(true),
  title: z.string().optional(),
  description: z.string().optional(),
  image: z.string().optional(),

  // AEO enhancement fields
  keywords: z.string().array().optional().describe("Keywords for SEO and AEO optimization"),
  author: z.string().optional().describe("Content author for attribution"),

  // Robots configuration
  robots: StorefrontSeoRobotsSchema.optional(),

  // Content classification
  articleType: z
    .enum(["product", "landing-page", "promotion", "collection", "brand-story", "editorial"])
    .optional()
    .describe("Content type classification for structured data"),

  // Social media handles
  twitterHandle: z.string().optional().describe("Twitter/X handle for Twitter Cards"),
});
export type StorefrontFunnelSeo = z.infer<typeof StorefrontFunnelSeoSchema>;

export const StorefrontFunnelSeoUpdateSchema = StorefrontFunnelSeoSchema.extend({
  enabled: StorefrontFunnelSeoSchema.shape.enabled.removeDefault().optional(),
});

export const IntegrationPopupProviderSchema = z.enum(["klaviyo", "postscript", "alia"]);
export type IntegrationPopupProvider = z.infer<typeof IntegrationPopupProviderSchema>;

export const IntegrationPopupsConfigSchema = z
  .object({
    enabled: z.boolean().optional(),
    providers: z.array(IntegrationPopupProviderSchema).optional(),
  })
  .optional();
export type IntegrationPopupsConfig = z.infer<typeof IntegrationPopupsConfigSchema>;

export const StorefrontFunnelConfigSchema = StorefrontThemeConfigSchema.extend({
  language: z.string().optional(),
  isShopProductPage: z.boolean().optional(),
  hideProductInVideo: z.boolean().optional(),
  hideProductPage: z.boolean().optional(),
  requiresSellingPlan: z.boolean().optional(),
  withoutDescription: z.boolean().optional(),
  variantSelectView: z.enum(["select", "chips"]).optional(),
  imageFit: z.enum(["cover", "contain"]).optional(),
  defaultSellingPlan: z.enum(["one-time", "subscription", "none"]).optional(),
  showOneImage: z.boolean().optional(),
  subscriptionTitle: z.string().optional(),
  globalSizeGuideMap: z
    .object({
      productIds: z.string().array(),
      allProducts: z.boolean(),
      sizeGuide: StorefrontPageSizeGuideSectionDataSchema,
    })
    .array()
    .optional(),
  withoutSubscription: z.boolean().optional(),
  sellingPlanUpgradeRules: z.array(CartSellingPlanUpgradeRuleSchema).optional(),
  pricePrefix: z.string().optional(),
  priceSuffix: z.string().optional(),
  starIcon: z.enum(["default", "7-stars"]).optional(),
  isCartEnabled: z.boolean().optional(),
  isStickyCTAEnabled: z.boolean().optional(),
  isDesktopStickyCTAEnabled: z.boolean().optional(),
  stickyCTA: StickyCTAActionConfigSchema.optional(), // Global sticky CTA config
  desktopStickyCTA: StickyCTAActionConfigSchema.optional(), // Desktop sticky CTA config
  seo: StorefrontFunnelSeoSchema.optional().default({ enabled: true }),
  excludeSubTotalCalculation: z.boolean().optional(),
  variantSelectModalStyle: z.enum(["default", "full-screen"]).optional(),
  showVariantSelectMediaGallery: z.boolean().optional(),
  integrationPopups: IntegrationPopupsConfigSchema,
});
export type StorefrontFunnelConfig = z.infer<typeof StorefrontFunnelConfigSchema>;

const AiDiscoveryPageSchema = AiDiscoveryFunnelContextSchema.extend({
  id: z.number(),
  link: z.string(),
});
export type AiDiscoveryPage = z.infer<typeof AiDiscoveryPageSchema>;

export const StorefrontFunnelSchema = z.object({
  id: z.number(),
  lastModified: z.string(),
  influencer: SourceEntitySchema.optional(),
  productsData: StorefrontPageProductDataSchema.array(),
  featuredProduct: StorefrontFunnelFeaturedProductSchema.optional(),
  shop: StorefrontShopSchema,
  program: StorefrontPageProgramSchema,
  stories: StorefrontMediaFileSchema.array(),
  media: StorefrontMediaFileSchema.array(),
  config: StorefrontFunnelConfigSchema.optional(),
  productSections: StorefrontPageSectionsSchema,
  cartSections: StorefrontPageSectionsSchema,
  storefrontAccessToken: z.string(),
  shopifyProxyToken: ShopifyProxyTokenSchema.optional(),
  experiment: StorefrontPageExperimentSchema.optional(),
  virtualProducts: StorefrontPageVirtualProductSchema.array(),
  // NEW: AST-based rendering support
  homePage: StorefrontPageTreeSchema,
  offers: StorefrontOfferSchema.array().optional(),
  aiDiscoveryPages: AiDiscoveryPageSchema.array().optional(),
  brandTokens: BrandTokensSchema.optional(),
  version: z.number(),
});
export type StorefrontFunnel = z.infer<typeof StorefrontFunnelSchema>;

/**
 * A global section or buy box rendered on its own for its editor.
 * `pageTheme` is the shop default it shows while it follows page themes.
 */
export const StorefrontSharedSectionPreviewSchema = StorefrontFunnelSchema.extend({
  pageTheme: SectionThemeSchema,
});
export type StorefrontSharedSectionPreview = z.infer<typeof StorefrontSharedSectionPreviewSchema>;

export const StorefrontSmartLinkTypeEnum = z.enum(["FUNNEL", "EXTERNAL_URL"]);
export type StorefrontSmartLinkType = z.infer<typeof StorefrontSmartLinkTypeEnum>;
export const StorefrontSmartLinkDetailsSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  slug: z.string(),
  url: z.string(),
  lastModified: z.string(),
  type: StorefrontSmartLinkTypeEnum,
});
export type StorefrontSmartLinkDetails = z.infer<typeof StorefrontSmartLinkDetailsSchema>;

export const StorefrontSmartLinkSchema = z.discriminatedUnion("type", [
  StorefrontSmartLinkDetailsSchema.extend({
    type: z.literal("FUNNEL"),
    funnel: z.object({
      seoEnabled: z.boolean().optional().default(true),
      id: z.number(),
      code: z.string(),
    }),
  }),
  StorefrontSmartLinkDetailsSchema.extend({
    type: z.literal("EXTERNAL_URL"),
    externalUrl: z.string(),
  }),
]);
export type StorefrontSmartLink = z.infer<typeof StorefrontSmartLinkSchema>;
